import React, { useLayoutEffect, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

/**
 * Props for the DraggableHeader component
 */
interface DraggableHeaderProps {
  /** Unique ID for the sortable item (usually column.key) */
  id: string;
  /** Whether the column can be reordered */
  isReorderable?: boolean;
  /** The alignment of the column content */
  align?: 'left' | 'center' | 'right';
  /** The content to be rendered within the draggable container */
  children: React.ReactNode;
}

/**
 * Component that wraps table header content to enable drag-and-drop reordering
 */
export const DraggableHeader: React.FC<DraggableHeaderProps> = ({ id, isReorderable = true, align, children }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useSortable({ 
    id, 
    disabled: !isReorderable 
  });

  /**
   * High-performance synchronization:
   * Publishes the current sortable transform to a CSS variable on the parent table.
   * This allows the Table body cells to shift concurrently with their headers.
   */
  useLayoutEffect(() => {
    if (containerRef.current) {
      const table = containerRef.current.closest('table');
      if (table) {
        const x = transform ? transform.x : 0;
        table.style.setProperty(`--col-drag-x-${id}`, `${x}px`);
      }
    }
  }, [transform, id]);

  const style = {
    // The visual shifts are now handled by the parent <th> in Table.tsx
    // to prevent "double-speed" movement.
    zIndex: isDragging ? 50 : 'auto',
  } as React.CSSProperties;

  /**
   * Prevents accidental sorting clicks when a drag interaction is intended
   */
  const handleClick = (e: React.MouseEvent) => {
    if (!isReorderable) return;
    // If we were dragging, prevent the click from reaching the sort handler in parent
    if (transform) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        (containerRef as any).current = node;
      }}
      style={style}
      {...(isReorderable ? attributes : {})}
      {...(isReorderable ? listeners : {})}
      onClickCapture={handleClick}
      className={`
        group flex items-center gap-1.5 h-full w-full min-w-0 select-none touch-none
        ${isReorderable ? 'cursor-grab active:cursor-grabbing' : ''}
        transition-all duration-200
      `}
    >
      {align === 'center' && (
        <div className="w-6 shrink-0" aria-hidden />
      )}
      {/* Main header content (Title, sorting icons) */}
      <div className={`flex-1 min-w-0 ${isReorderable ? 'pointer-events-none' : ''}`}>
        {children}
      </div>

      {/* Drag handle — always shown; reordering follows column.isReorderable */}
      <div
        className={`
          flex shrink-0 items-center justify-center p-1
          transition-opacity duration-200
          ${
            isReorderable
              ? "opacity-40 group-hover:opacity-100"
              : "opacity-25 pointer-events-none"
          }
        `}
      >
        <GripVertical size={14} className="text-slate-400" aria-hidden />
      </div>
    </div>
  );
};
